import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Dialog, DialogContent, TextField, Typography } from "@mui/material";
import { cancelAudit, unwrapPost } from "../../../../helpers/auditHub_helper";
import { AH_ACCENT, AH_TEXT, AH_WHITE, auditHubPageSx } from "../theme";

/** CancelAuditRequest.reason */
const REASON_MAX = 500;

const LABEL_COLOR = "#334155";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    color: LABEL_COLOR,
    backgroundColor: AH_WHITE,
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: AH_TEXT, borderWidth: "1px" },
    "&.Mui-focused": { boxShadow: `0 0 0 1px ${AH_TEXT}` },
  },
  "& .MuiInputBase-input": { fontSize: "0.875rem", color: LABEL_COLOR },
  "& .MuiInputBase-input::placeholder": { color: "#74788d", opacity: 1 },
  "& .MuiFormHelperText-root": { mx: 0, fontSize: "0.75rem", color: "#74788d", textAlign: "right" },
};

const dialogButtonSx = {
  minWidth: 140,
  height: 38,
  px: 2,
  borderRadius: "4px",
  textTransform: "none",
  fontWeight: 400,
  fontSize: "0.875rem",
  boxShadow: "none",
  "&:hover": { boxShadow: "none" },
};

const CancelAuditDialog = ({ audit, open, onClose, onCancelled }) => {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setReason("");
    setSaving(false);
    setError("");
  }, [open, audit?.auditId]);

  const trimmed = reason.trim();

  const confirm = async () => {
    if (!audit?.auditId || !trimmed || saving) return;
    setSaving(true);
    setError("");
    try {
      unwrapPost(await cancelAudit(audit.auditId, { reason: trimmed }));
      onCancelled(audit);
    } catch (err) {
      setError(err?.message || "Unable to cancel this assignment.");
    } finally {
      setSaving(false);
    }
  };

  const label = [audit?.auditCode, audit?.name].filter(Boolean).join(" · ");

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="cancel-audit-title"
      PaperProps={{ sx: { ...auditHubPageSx, borderRadius: "12px", overflow: "hidden", maxWidth: 600 } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          minHeight: 56,
          px: 3,
          backgroundColor: AH_ACCENT,
        }}
      >
        <Typography id="cancel-audit-title" sx={{ fontWeight: 700, fontSize: "1.0625rem", color: AH_WHITE, lineHeight: 1.3 }}>
          Cancel Assignment
        </Typography>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          disabled={saving}
          onClick={onClose}
          sx={{
            p: 0,
            m: 0,
            border: "none",
            background: "transparent",
            color: AH_WHITE,
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            cursor: saving ? "default" : "pointer",
          }}
        >
          ×
        </Box>
      </Box>
      <DialogContent sx={{ px: 3, py: 2.5, backgroundColor: AH_WHITE }}>
        <Typography sx={{ fontSize: "0.875rem", color: AH_TEXT, lineHeight: 1.6, overflowWrap: "anywhere" }}>
          Are you sure you want to cancel{" "}
          {label ? (
            <Box component="span" sx={{ fontWeight: 600 }}>
              {label}
            </Box>
          ) : (
            "this assignment"
          )}
          ?
        </Typography>
        <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", lineHeight: 1.6, mt: 1 }}>
          Cancelling stops all of its scans and moves it to Completed &amp; Archived Assignments. This cannot be undone.
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Box
            component="label"
            htmlFor="cancel-audit-reason"
            sx={{ display: "block", mb: "6px", fontSize: "0.8125rem", fontWeight: 600, color: LABEL_COLOR }}
          >
            Reason
            <Box component="span" sx={{ color: "#dc2626", ml: 0.25 }} aria-hidden="true">
              *
            </Box>
          </Box>
          <TextField
            id="cancel-audit-reason"
            required
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            autoFocus
            value={reason}
            disabled={saving}
            onChange={(event) => {
              setReason(event.target.value);
              if (error) setError("");
            }}
            placeholder="Why is this assignment being cancelled?"
            inputProps={{ maxLength: REASON_MAX }}
            helperText={`${reason.length}/${REASON_MAX}`}
            sx={fieldSx}
          />
        </Box>
      </DialogContent>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: { xs: "wrap", sm: "nowrap" },
          gap: 1.5,
          px: 3,
          py: 2,
          borderTop: "1px solid #e5e7eb",
          backgroundColor: "#f8fafc",
        }}
      >
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{
            ...dialogButtonSx,
            backgroundColor: "#74788d",
            color: "#eff2f7",
            "&:hover": { backgroundColor: "#636678", boxShadow: "none" },
          }}
        >
          Close
        </Button>
        {error ? (
          <Typography
            role="alert"
            sx={{
              flex: 1,
              minWidth: 0,
              order: { xs: 3, sm: 0 },
              flexBasis: { xs: "100%", sm: "auto" },
              fontSize: "0.8125rem",
              lineHeight: 1.45,
              color: "#dc2626",
              textAlign: "center",
              overflowWrap: "anywhere",
            }}
          >
            {error}
          </Typography>
        ) : null}
        <Button
          onClick={confirm}
          disabled={saving || !trimmed || !audit?.auditId}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            ...dialogButtonSx,
            backgroundColor: "#dc2626",
            color: AH_WHITE,
            "&:hover": { backgroundColor: "#b91c1c", boxShadow: "none" },
            "&.Mui-disabled": { backgroundColor: "#dc2626", color: AH_WHITE, opacity: 0.65 },
          }}
        >
          {saving ? "Cancelling" : "Cancel Assignment"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default CancelAuditDialog;
